import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Settings,
  Tag,
  Users,
  LogOut,
  Store,
  Menu,
  Layers,
  Image as ImageIcon,
  Ticket,
  BarChart3,
  Ruler,
  Sparkles,
  Undo2,
  Banknote,
  Megaphone,
  Scissors,
  CalendarCheck,
  CalendarDays,
  Clapperboard,
  UserCircle2,
  Palette,
  Bell,
  CreditCard,
  Globe,
  Sliders,
  IndianRupee,
  UserCheck,
  Upload,
  Wand2,
  Wallet,
  Rocket,
  MessageCircle,
  Smartphone,
  Receipt,
  Flame,
  ArrowRightLeft,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../context/BrandingContext";
import { useFeatures } from "../../context/FeaturesContext";
import NotificationBell from "../common/NotificationBell";
import WalletBadge from "../common/WalletBadge";
import TrialBanner from "../common/TrialBanner";
import ImpersonationBanner from "../common/ImpersonationBanner";
import ExpiredTenantGate from "../common/ExpiredTenantGate";
import ViewStorefrontButton from "../common/ViewStorefrontButton";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";

const SELLER_NAV = [
  { to: "/seller", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/seller/products", label: "Products", icon: Package },
  { to: "/seller/inventory", label: "Inventory", icon: Layers },
  { to: "/seller/orders", label: "Orders", icon: ShoppingBag },
  { to: "/seller/coupons", label: "Coupons", icon: Ticket },
  { to: "/seller/analytics", label: "Analytics", icon: BarChart3, feature: "advanced_analytics" },
  { to: "/seller/appointments", label: "Appointments", icon: CalendarCheck, feature: "stitching_appointments" },
  { to: "/seller/settings", label: "Settings", icon: Settings },
];

const ADMIN_NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/customers", label: "Customers", icon: UserCheck },
  { to: "/admin/sellers", label: "Sellers", icon: Store },
  { to: "/admin/categories", label: "Categories", icon: Tag },
  { to: "/admin/attributes", label: "Attributes", icon: Layers },
  { to: "/admin/size-charts", label: "Size Charts", icon: Ruler },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/products/bulk-upload", label: "Bulk Upload", icon: Upload },
  { to: "/admin/products/smart-catalog", label: "Smart Catalog", icon: Wand2 },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/returns", label: "Returns", icon: Undo2 },
  { to: "/admin/refunds", label: "Refunds", icon: Banknote },
  { to: "/admin/banners", label: "Banners", icon: ImageIcon },
  { to: "/admin/sections", label: "Sections", icon: Sparkles },
  { to: "/admin/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/admin/stitching", label: "Stitching", icon: Scissors, feature: "measurements" },
  { to: "/admin/appointments", label: "Appointments", icon: CalendarCheck, feature: "stitching_appointments" },
  { to: "/admin/appointment-slots", label: "Slot Calendar", icon: CalendarDays, feature: "stitching_appointments" },
  { to: "/admin/social", label: "Social Reels", icon: Clapperboard, feature: "social_reels" },
  { to: "/admin/creators", label: "Creators", icon: UserCircle2, feature: "social_reels" },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, feature: "advanced_analytics" },
  { to: "/admin/branding", label: "Branding", icon: Palette },
  { to: "/admin/push", label: "Push Broadcasts", icon: Bell, feature: "push_broadcasts" },
  { to: "/admin/billing", label: "Billing", icon: CreditCard },
  { to: "/admin/upgrade", label: "Upgrade Plan", icon: Rocket },
  { to: "/admin/wallet", label: "Wallet", icon: Wallet },
  { to: "/admin/domain", label: "Domain & App", icon: Globe },
  { to: "/admin/pingbix", label: "SMS & WhatsApp", icon: MessageCircle },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

const SUPER_NAV = [
  { to: "/superadmin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/superadmin/tenants", label: "Tenants", icon: Users },
  { to: "/superadmin/features", label: "Features", icon: Sliders },
  { to: "/superadmin/plans", label: "Plans", icon: IndianRupee },
  { to: "/superadmin/analytics", label: "Revenue", icon: BarChart3 },
  { to: "/superadmin/wallets", label: "Wallets", icon: Wallet },
  { to: "/superadmin/pricing", label: "Pricing", icon: Tag },
  { to: "/superadmin/subscriptions", label: "Subscriptions", icon: Receipt },
  { to: "/superadmin/pwa", label: "Mobile Apps", icon: Smartphone },
  { to: "/superadmin/firebase", label: "Push (FCM)", icon: Flame },
  { to: "/superadmin/payments", label: "Payments", icon: CreditCard },
  { to: "/superadmin/migrate-tenant", label: "Migrate Tenant", icon: ArrowRightLeft },
  { to: "/superadmin/settings", label: "Settings", icon: Settings },
];

function SidebarBody({ variant, nav, onNavigate }) {
  const { user, seller, logout } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="p-6 border-b border-[hsl(var(--kb-border))] shrink-0">
        <Link to="/" className="font-display text-2xl" onClick={onNavigate}>
          {branding?.brand_name || "KalkiBazaar"}
        </Link>
        <div className="overline mt-2">
          {variant === "admin" ? "Admin Console" : variant === "super" ? "Super Admin" : "Seller Studio"}
        </div>
      </div>
      <nav
        className="flex-1 p-3 overflow-y-auto min-h-0 kb-scroll-hide"
        data-testid={`${variant}-nav-scroll`}
      >
        {nav.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={onNavigate}
              data-testid={`${variant}-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm rounded-sm transition-colors ${
                  isActive
                    ? "bg-[hsl(var(--kb-primary))] text-white"
                    : "text-[hsl(var(--kb-text))] hover:bg-[hsl(var(--kb-surface-alt))]"
                }`
              }
            >
              <Icon size={16} />
              {n.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-4 border-t border-[hsl(var(--kb-border))] text-sm shrink-0">
        <div className="flex items-center gap-2 mb-3">
          <Store size={14} />
          <div className="truncate min-w-0">
            <div className="font-medium truncate">
              {seller?.shop_name || user?.name || user?.phone}
            </div>
            <div className="text-xs text-[hsl(var(--kb-muted))] capitalize">{user?.role}</div>
          </div>
        </div>
        <button
          onClick={() => {
            logout();
            navigate("/");
          }}
          className="flex items-center gap-2 text-xs text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))]"
          data-testid="dashboard-logout"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}

export default function DashboardLayout({ variant, children }) {
  const nav = variant === "admin" ? ADMIN_NAV : variant === "super" ? SUPER_NAV : SELLER_NAV;
  const { has: hasFeature, loading: featsLoading } = useFeatures();
  // Gate nav items by `feature` key. When features haven't loaded yet we
  // optimistically show everything, then filter once the list arrives.
  const filteredNav = featsLoading
    ? nav
    : nav.filter((n) => !n.feature || hasFeature(n.feature));
  const [openMobile, setOpenMobile] = useState(false);
  const { branding } = useBranding();

  return (
    <div className="min-h-screen md:grid md:grid-cols-[240px_1fr] bg-[hsl(var(--kb-dashboard-bg))]">
      <aside className="hidden md:flex md:flex-col md:sticky md:top-0 md:h-screen bg-white border-r border-[hsl(var(--kb-border))] safe-pt safe-pb safe-pl">
        <SidebarBody variant={variant} nav={filteredNav} />
      </aside>

      {/* Mobile top bar — reserves space for the iOS status bar / Android
          notch so the brand + menu icon sit cleanly below system chrome,
          matching Flutter's SafeArea behaviour. */}
      <div className="md:hidden flex items-center justify-between bg-white border-b border-[hsl(var(--kb-border))] p-4 safe-area-top safe-pl safe-pr">
        <Link to="/" className="font-display text-xl">{branding?.brand_name || "KalkiBazaar"}</Link>
        <button
          onClick={() => setOpenMobile(true)}
          className="p-2"
          aria-label="Open navigation"
          data-testid={`${variant}-mobile-nav-btn`}
        >
          <Menu size={20} />
        </button>
      </div>

      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent side="left" className="p-0 w-72">
          <SheetHeader className="sr-only">
            <SheetTitle>Dashboard navigation</SheetTitle>
            <SheetDescription>Switch between sections</SheetDescription>
          </SheetHeader>
          <SidebarBody variant={variant} nav={filteredNav} onNavigate={() => setOpenMobile(false)} />
        </SheetContent>
      </Sheet>

      <main className="p-4 md:p-10 overflow-x-hidden safe-pb safe-pl safe-pr">
        <ImpersonationBanner />
        {variant === "admin" && (
          <>
            <TrialBanner />
            <div className="flex justify-end items-center gap-3 mb-2">
              <ViewStorefrontButton />
              <WalletBadge />
              <NotificationBell />
            </div>
          </>
        )}
        {variant === "admin" ? (
          <ExpiredTenantGate>{children}</ExpiredTenantGate>
        ) : (
          children
        )}
      </main>
    </div>
  );
}
